import template from './ps-table.html';
import
{
	PSTableDataSourceApiTable,
	PSTableDataSourceDirect,
	PSTableDataSourceCallback
} from './data-sources/index.js';
import PSTablePaging from './features/PSTablePaging.js';
import PSTableSorting from './features/PSTableSorting.js';
import PSTableFiltering from './features/PSTableFiltering.js';
import PSTableField from './features/PSTableField.js';
import PSCard from './features/PSCard.js';

/**
 * ViewModel for the PS Table component.
 *
 * PSTableViewModel manages the complete table functionality including data sources,
 * pagination, sorting, filtering, field management, row selection, and actions.
 * It automatically detects the data source type and creates the appropriate
 * data source instance.
 *
 * @class PSTableViewModel
 * @example
 * // Basic usage with array data
 * const viewModel = new PSTableViewModel({
 *   data_source: [
 *     { id: 1, name: 'John', age: 30 },
 *     { id: 2, name: 'Jane', age: 25 }
 *   ],
 *   fields: [
 *     { field: 'name', label: 'Name' },
 *     { field: 'age', label: 'Age' }
 *   ]
 * });
 *
 * // Advanced usage with API data source
 * const viewModel = new PSTableViewModel({
 *   data_source: { schema: 'public', table: 'users' },
 *   fields: [
 *     { field: 'name', label: 'Name', sortable: true },
 *     { field: 'email', label: 'Email' }
 *   ],
 *   actions: [
 *     { name: 'edit', label: 'Edit', on_click: (row) => editUser(row) }
 *   ],
 *   multi_select: true,
 *   on_row_click: (row) => console.log('Row clicked:', row)
 * });
 */
class PSTableViewModel
{
	/**
	 * Creates a new PSTableViewModel instance.
	 *
	 * @param {Object} params - Table configuration parameters
	 * @param {string} [params.local_storage_name=null] - Local storage name for table state
	 * @param {Array<Object>|Object|Function} params.data_source - Data source (array, API config, or callback function)
	 * @param {Array<Object>} [params.fields=[]] - Field configurations for table columns
	 * @param {Array<Object>} [params.actions=[]] - Action configurations for table actions
	 * @param {Array<Object>} [params.global_actions=[]] - Table action configurations for global actions
	 * @param {Array<Object>} [params.bulk_actions=[]] - Bulk action configurations for table actions
	 * @param {string} [params.display_mode='table'] - Display mode ('table' or 'card')
	 * @param {boolean} [params.multi_select=false] - Whether to allow multiple row selection
	 * @param {Array<Object>} [params.selected_rows=[]] - List of selected rows, for manipulation outside of the table.
	 * @param {boolean} [params.allow_download=true] - Whether to show the download button
	 * @param {boolean} [params.allow_refresh=true] - Whether to show the refresh button
	 * @param {boolean} [params.allow_modify_fields=true] - Whether to show the modify fields button
	 * @param {boolean} [params.force_refresh=false] - Observable that forces a data refresh when changed to true
	 * @param {Object} [params.paging_options] - Pagination configuration options
	 * @param {Object} [params.sorting_options] - Sorting configuration options
	 * @param {Object} [params.filtering_options] - Filtering configuration options
	 * @param {Object} [params.card_component] - Card component configuration for card display mode
	 * @param {Function} [params.on_row_click] - Callback function for row click events, where allowed
	 * @param {boolean} [params.subrow_spanned] - Whether to merge all columns into one for the subrow
	 * @param {Function} [params.subrow_callback] - Callback function for subrow rendering
	 * @param {Object} [params.subrow_component] - Component configuration for subrow rendering
	 * @param {Object} [params.subrow_component.params] - Optional parameters for subrow component
	 * @param {Function|boolean|Object} [params.subrow_component.visible] - Optional visibility rule for the subrow component.
	 */
	constructor(params)
	{
		this.params = params;

		// Original input fields. DO NOT USE THESE DIRECTLY. They are used to create computed observables.
		this.original_data_source = ko_helper.safe_observable(params.data_source, []);
		this.original_fields = ko_helper.safe_observable(params.fields, []);
		this.original_display_mode = ko_helper.safe_observable(params.display_mode, 'table');

		this.local_storage_name = ko_helper.safe_observable(params.local_storage_name, null);
		this.actions = ko_helper.safe_observable(params.actions, []);
		this.global_actions = ko_helper.safe_observable(params.global_actions, []);
		this.bulk_actions = ko_helper.safe_observable(params.bulk_actions, []);
		this.multi_select = ko_helper.safe_observable(params.multi_select, false);
		this.selected_rows = ko_helper.safe_observable(params.selected_rows, []);
		this.allow_modify_fields = ko_helper.safe_observable(params.allow_modify_fields, true);
		this.allow_download = ko_helper.safe_observable(params.allow_download, true);
		this.allow_refresh = ko_helper.safe_observable(params.allow_refresh, true);
		this.force_refresh = ko_helper.safe_observable(params.force_refresh, false);
		this.force_refresh.subscribe(force_refresh =>
		{
			if (force_refresh)
				this.updateData();
		});

		// Data_source handling
		this.update_in_progress = false;
		this.is_dirty = false;

		this.data_source = ko.observable();
		this.original_data_source.subscribe(() => this.setup_data_source());

		this.all_rows_selected = ko.pureComputed(() =>
		{
			return this.selected_rows().length == this.records().length;
		});

		this.column_count = ko.pureComputed(() =>
		{
			let fields = this.fields().length;
			if (this.multi_select())
				fields++;
			if (this.actions().length > 0)
				fields++;

			return fields;
		});

		this.selected_text = ko.pureComputed(() =>
		{
			return this.selected_rows().length > 0 && this.multi_select() ? ` (${this.selected_rows().length} selected)` : '';
		});

		// Standard Variables. Used in the component.
		this.records = ko.observableArray(); // List of records to display.
		this.auto_fields = ko.observableArray(); // Auto-generate fields from data source.
		this.fields = ko.observableArray(); // All fields.
		this.visible_field_names = ko.observableArray();
		this.loading = ko.observable(false); // Show loading indicator.
		this.error_message = ko.observable(); // Show error indicator

		// Subrow rendering
		this.subrow_spanned = ko_helper.safe_observable(params.subrow_spanned, true);
		this.subrow_callback = ko_helper.safe_observable(params.subrow_callback, null);
		this.subrow_component = ko_helper.safe_observable(params.subrow_component, null);

		// TODO: Find fix for page_size changing active page_number and loading data twice.
		// Pagination configuration
		params.paging_options ??= {};
		params.paging_options.selected_rows ??= this.selected_rows;
		this.paging = new PSTablePaging(params.paging_options);
		this.paging.page_size.subscribe(page_size =>
		{
			this.updateData();
		});
		this.paging.page_number.subscribe(page_number =>
		{
			this.updateData();
		});

		// TODO: Find fix for sorting loading data twice.
		// Sorting configuration
		this.sorting = new PSTableSorting(params.sorting_options ?? params);
		this.sorting.sortfield.subscribe(sortfield =>
		{
			this.updateData();
		});
		this.sorting.sortorder.subscribe(sortorder =>
		{
			this.updateData();
		});

		// TODO: Find fix for filtering loading data twice.
		// Filtering configuration
		this.filtering = new PSTableFiltering(params.filtering_options ?? params);
		this.filtering.permanent_filters.subscribe(permanent_filters =>
		{
			this.updateData();
		});
		this.filtering.filters.subscribe(filters =>
		{
			this.updateData();
		});

		this.cardHandler = new PSCard(params.card_component ?? params);

		// Fields configuration
		this.fields = ko.observableArray();
		this.original_fields.subscribe(() => this.updateFields());
		this.auto_fields.subscribe(() => this.updateFields());

		this.visible_fields = ko.pureComputed(() =>
		{
			let fields = this.fields() || [];
			let visible_field_names = this.visible_field_names() || [];
			let visible_fields = [];

			if (visible_field_names.length > 0)
			{
				visible_fields = visible_field_names.map(name => fields.find(f => f.name() == name));
				visible_fields = visible_fields.filter(f => f !== undefined);
			}
			else
			{
				visible_fields = fields;
			}

			return visible_fields;
		});

		// Table Events
		this.on_row_click = params.on_row_click;

		// Load local storage
		this.load_local_storage();

		// Initialize data
		this.setup_data_source();

		this.visible_field_names.equalityComparer = (oldValue, newValue) =>
		{
			let isEqual = oldValue && newValue && oldValue.length == newValue.length && oldValue.every((value, index) => value == newValue[index]);
			return isEqual;
		};
		this.visible_field_names.subscribe(() => this.save_local_storage());
	}

	/**
	 * Loads the table configuration from local storage.
	 */
	load_local_storage()
	{
		if (this.local_storage_name())
		{
			let data = localStorage.getItem(this.local_storage_name());
			data = JSON.parse(data);

			if (data)
			{
				if (data.visible_field_names && data.visible_field_names.length > 0)
					this.visible_field_names(data.visible_field_names);
				// TODO: Parse and set the table data from local storage
			}
		}
	}

	// TODO: Determine when exactly the local storage should be updated
	/**
	 * Saves the table configuration to local storage.
	 */
	save_local_storage()
	{
		if (this.local_storage_name())
		{
			let data = {
				visible_field_names: this.visible_field_names()
			};
			if (data)
			{
				localStorage.setItem(this.local_storage_name(), JSON.stringify(data));
				// TODO: Parse and set the table data to local storage
			}
		}
	}

	/**
	 * Sets up the data source for the table. This is called initially and when the data_source is changed.
	 */
	setup_data_source()
	{
		let data_source = this.original_data_source();
		let options = {
			data_source: data_source,
			paging: this.paging,
			sorting: this.sorting,
			filtering: this.filtering
		};

		let ds = null;
		if (data_source instanceof Array)
			ds = new PSTableDataSourceDirect(options);
		else if (data_source?.schema && data_source?.table)
			ds = new PSTableDataSourceApiTable(options);
		else if (data_source instanceof Function)
			ds = new PSTableDataSourceCallback(options);
		else
			return null;

		this.data_source(ds);
		this.updateData();
	}

	/**
	 * Safely wraps the on_row_click callback function.
	 *
	 * @param {Object} row - The row data that was clicked
	 * @param {Event} event - The DOM event that triggered the click
	 */
	async onFieldClick(field, row, event)
	{
		if (field.on_click instanceof Function)
			return await field.on_click(field, row, event);
		if (!field.clickable())
			return;
		if (this.on_row_click instanceof Function)
			await this.on_row_click(row, event);
		else
			return;
	}

	/**
	 * Resolves CSS classes for a table cell.
	 *
	 * Supports:
	 * - static class string
	 * - class callback `(row, field) => string`
	 * - class object map `{ className: condition }`
	 * - observables/computes resolving to any of the above
	 *
	 * Example:
	 * classes: 'ps-text-danger'
	 *
	 * classes: (row) => row.amount < 0 ? 'ps-text-danger' : ''
	 *
	 * classes: {
	 *   'ps-text-danger': (row) => row.amount_type === 'credit',
	 *   'ps-text-success': (row) => row.amount_type === 'debit'
	 * }
	 *
	 * @param {Object} field - Field definition object
	 * @param {Object} row - Current row data
	 * @returns {string|Object|null}
	 */
	getFieldClasses(field, row)
	{
		const classes = ko.unwrap(field?.classes);

		if (!classes)
			return null;

		if (typeof classes === 'function')
			return classes(row, field);

		if (typeof classes === 'object' && !Array.isArray(classes))
		{
			return Object.entries(classes)
				.filter(([className, condition]) =>
					typeof condition === 'function' ? condition(row, field)	: ko.unwrap(condition)
				)
				.map(([className]) => className)
				.join(' ');
		}

		return classes;
	}

	/**
	 * Resolves inline styles for a table cell.
	 *
	 * Field definitions may specify `style` as:
	 * - a static style object or string
	 * - a function returning a style object/string
	 * - an observable/computed resolving to either of the above
	 *
	 * If `style` resolves to a function, it is executed with `(row, field)`
	 * so that styles can be determined dynamically based on row data.
	 *
	 * Examples:
	 *   style: { 'text-align': 'right' }
	 *   style: (row) => row.total < 0 ? { color: 'red' } : null
	 *
	 * @param {Object} field - Field definition object
	 * @param {Object} row - Current row data
	 * @returns {Object|string|null} Inline style value compatible with Knockout's `style` binding
	 */
	getFieldStyle(field, row)
	{
		const style = ko.unwrap(field?.style);
		return typeof style === 'function' ? style(row, field) : style;
	}

	/**
	 * Toggles row selection for single or all rows.
	 *
	 * @param {Object|null} row - The row to toggle selection for, or null to toggle all rows
	 * @returns {boolean} Always returns true
	 */
	toggleRowSelection(row)
	{
		let selection = this.selected_rows() ?? [];

		// Toggle all rows
		if (!row)
		{
			if (selection.length === (this.records() ?? []).length)
				selection = [];
			else
				selection = (this.records() ?? []).map(r => r) ?? [];
		}
		else // Toggle single row
		{
			if (selection.indexOf(row) > -1)
				selection.splice(selection.indexOf(row), 1);
			else
				selection.push(row);
		}

		this.selected_rows(selection);
		return true;
	}

	/**
	 * Updates the table data by fetching from the current data source.
	 *
	 * Handles loading states, error handling, and clears selected rows.
	 * Also triggers auto field generation based on the new data.
	 */
	async updateData()
	{
		// This block handles cases where update requests were made while an update is already in progress.
		if (this.update_in_progress)
		{
			this.is_dirty = true;
			return;
		}
		this.update_in_progress = true;

		this.force_refresh(false); // Reset force_refresh to allow it to be called again. This is kept at the start since a second refresh may be needed before this function completes.
		this.loading(true);
		let data_source = this.data_source();

		if (data_source)
		{
			try
			{
				this.error_message(null);
				let records = await data_source.select();
				this.records(records);
			}
			catch (error)
			{
				// Show error message to the user.
				this.error_message(error.message);
				console.error('ERROR:', error);

				this.records([]);
			}
		}
		else
			this.records([]);

		// TODO: Check if this should happen here, or as part of the data_source.select()
		this.selected_rows([]);

		this.updateAutoFields();
		this.loading(false);

		// This block handles cases where update requests were ignored, and debounces them by calling updateData again.
		this.update_in_progress = false;
		if (this.is_dirty)
		{
			this.is_dirty = false;
			await this.updateData();
		}
	}

	/**
	 * Updates the auto-generated fields based on the current data.
	 *
	 * Extracts all unique field names from the data records and stores them
	 * for use in field generation.
	 */
	updateAutoFields()
	{
		let data = this?.records() ?? [];
		let fields = {};

		data.forEach(record =>
		{
			if (!record || typeof record !== 'object')
				return;

			Object.keys(record).forEach(field =>
			{
				fields[field] = true;
			});
		});

		fields = Object.keys(fields);

		this.auto_fields(fields);
	}

	/**
	 * Updates the table fields configuration.
	 *
	 * Processes the original fields configuration, handles auto field generation
	 * with wildcard (*) support, and creates PSTableField instances.
	 */
	updateFields()
	{
		let fields = this.original_fields() ?? [];

		if (fields.length == 0)
		{
			fields = this.auto_fields() ?? [];
		}
		else
		{
			// shallow copy the fields so we don't modify the original array
			fields = fields.map(field =>
			{
				return field;
			});

			// Add auto fields to the fields list if a field is defined with a *
			let auto_field_index = fields.findIndex(field => field.field == '*');
			let auto_field = fields[auto_field_index];
			if (auto_field_index > -1)
			{
				let auto_template = {};
				if (auto_field instanceof Object)
					auto_template = Object.assign(auto_template, auto_field);

				let extra_fields = this.auto_fields().filter(field => !fields.some(f => f.field == field));
				extra_fields = extra_fields.map(field =>
				{
					let field_def = Object.assign({}, auto_template);
					field_def.field = field;

					return field_def;
				});

				fields.splice(auto_field_index, 1, ...extra_fields); // Replace auto_field with extra field entries
			}
		}

		let visible_field_names = this.visible_field_names();
		if (visible_field_names.length > 0)
		{
			fields.forEach(f =>
			{
				let visible = visible_field_names.includes(ko.unwrap(f.name));
				if (ko.isObservable(f.visible)) f.visible(visible);
				else f.visible = visible;
			});
		}

		fields = fields.map(field => new PSTableField(field));
		fields.forEach(f => f.visible.subscribe(v => this.visible_field_names(this.calc_visible_fields(fields))));

		// Recalculate fields to match current field object's visibility
		// this.visible_field_names(this.calc_visible_fields(fields));

		this.fields(fields);
	}

	calc_visible_fields(fields)
	{
		let visible_fields = fields.filter(field => field.visible());
		if (visible_fields.length > 0)
			return visible_fields.map(field => field.name());
		else
			return [];
	}

	/**
	 * Builds the subrow component config for a specific row.
	 *
	 * Supports an optional `visible` flag on `subrow_component`:
	 * - function: called with `(row, index)` and must return truthy to render
	 * - observable: unwrapped and must be truthy to render
	 * - boolean: must be `true` to render
	 *
	 * If `visible` is omitted, the subrow component is rendered.
	 * If visibility fails, returns null so no subrow component is rendered for that row.
	 *
	 * @param {Object} row - The row data for the subrow
	 * @param {number} index - The row index
	 * @returns {Object|null} Component config ({ name, params }) or null when hidden/invalid
	 */
	build_subrow_component(row, index)
	{
		const original = ko.unwrap(this.subrow_component);
		if (!original || typeof original !== 'object')
			return null;

		const visible = original.visible;
		if (visible !== undefined)
		{
			const result = typeof visible === 'function' ? visible(row, index) : ko.unwrap(visible);

			if (!result)
				return null;
		}

		const params = Object.assign({}, original.params || {}, {
			data: row,
			index: index
		});
	
		return {
			name: original.name,
			params: params
		};
	}

	/**
	 * Downloads the table data as a CSV file.
	 *
	 * Filters out non-visible fields.
	 */
	async download()
	{
		let visible_fields = ko.mapping.toJS(this.visible_fields());
		let csv = visible_fields.map(f => f.name || f.field).join(',') + '\n';

		this.records().forEach(record =>
		{
			let row = visible_fields.map(f =>
			{
				let val = record[f.field];
				val = val ?? '';

				if (typeof val === 'object')
					val = JSON.stringify(val);

				if (typeof val === 'string')
				{
					val = val.replace(/"/g, '""');

					if (!val)
						return '';
					else if (val.includes(','))
						return `"${val}"`;
					else
						return val;
				}
				else return val;
			});
			csv += row.join(',') + '\n';
		});

		// Download file
		let blob = new Blob([csv], { type: 'text/csv' });
		let url = URL.createObjectURL(blob);
		let a = document.createElement('a');
		a.href = url;
		a.download = `Download_${new Date().toISOString()}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	}
}

/**
 * PS Table component configuration.
 *
 * @type {Object}
 */
export default {
	name: 'ps-table',
	viewModel: PSTableViewModel,
	provider: 'ko',
	template: template
};
